import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Printer } from 'lucide-react';
import { NAVY, GOLD } from '../utils/constants';

// ── Number to words (Indian system) ──────────────────────────────────────────
const ONES = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
  'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
  'Seventeen','Eighteen','Nineteen'];
const TENS = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];

const cvtHundreds = (n) => {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n/10)] + (n%10 ? ' ' + ONES[n%10] : '');
  return ONES[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' and ' + cvtHundreds(n%100) : '');
};

const amountToWords = (amount) => {
  const n = Math.round(amount);
  if (n === 0) return 'Zero Rupees Only';
  const crore    = Math.floor(n / 10000000);
  const lakh     = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rem      = n % 1000;
  let result = '';
  if (crore)    result += cvtHundreds(crore)    + ' Crore ';
  if (lakh)     result += cvtHundreds(lakh)     + ' Lakh ';
  if (thousand) result += cvtHundreds(thousand) + ' Thousand ';
  if (rem)      result += cvtHundreds(rem);
  return 'Rupees ' + result.trim() + ' Only';
};

// ── Date helpers ──────────────────────────────────────────────────────────────
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const ordinal = (n) => { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };

const fmtYMD = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  return `${ordinal(d)} ${MONTHS[m-1]} ${y}`;
};

const f2  = (n) => Number(n || 0).toFixed(2);
const fI  = (n) => Number(Math.round(n || 0)).toLocaleString('en-IN');

// ── Reusable table primitives (mirrors InvoicePrint) ──────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '7px 10px',
      textAlign: align,
      fontWeight: 700,
      fontSize: 9.5,
      color: '#5a4a38',
      background: '#f3f0ea',
      borderBottom: '1.5px solid #d8d0c0',
      borderTop: '1.5px solid #d8d0c0',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  );
}

function Td({ children, align = 'left', bold, shade }) {
  return (
    <td style={{
      padding: '6px 10px',
      textAlign: align,
      fontSize: 10,
      color: NAVY,
      fontWeight: bold ? 700 : 400,
      borderBottom: '1px solid #f0ebe0',
      background: shade ? '#fafaf8' : 'transparent',
    }}>
      {children}
    </td>
  );
}

function GoldDivider() {
  return <div style={{ height: 2, background: GOLD, width: '100%' }} />;
}

function InfoRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 3, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 10, color: '#7a6a50', fontWeight: 600, minWidth: 88, flexShrink: 0 }}>{label}:</span>
      <span style={{ fontSize: 10, color: NAVY, fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}

function TotalRow({ label, value, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: big ? '7px 0' : '4px 0', borderBottom: big ? 'none' : '1px solid #f0ebe0' }}>
      <span style={{ fontSize: big ? 12 : 10, color: big ? NAVY : '#5a4a38', fontWeight: big ? 800 : 600 }}>{label}</span>
      <span style={{ fontSize: big ? 13 : 10, color: big ? GOLD : NAVY, fontWeight: big ? 800 : 600 }}>₹{value}</span>
    </div>
  );
}

// ── Credit note content (forwardRef so useReactToPrint can access the node) ──
const CreditNoteContent = React.forwardRef(function CreditNoteContent({ creditNoteData }, ref) {
  const {
    creditNoteNumber, creditNoteDate, kind, reason,
    invoiceNumber, invoiceDate, invoiceType, customerName, customerPhone,
    partner_gstin, partner_address, partner_state, partner_state_code,
    cruiseType, cruiseDisplayName, deck, adults, children,
    taxableCruise, cgstCruise, sgstCruise,
    taxableFood, cgstFood, sgstFood,
    totalTax, grandTotal, settings,
  } = creditNoteData;

  const isB2B = invoiceType === 'b2b';

  const companyName = settings?.company_name || 'Brahmaputra Cruise Private Limited';
  const address     = (settings?.address     || '').replace(/\n/g, ', ');
  const gstin       = settings?.gstin        || '';
  const phone       = settings?.phone        || '';
  const email       = settings?.email        || '';
  const sacCruise   = settings?.cruise_sac_code || '996415';
  const sacFood     = settings?.food_sac_code   || '996331';

  const hasFood = (Number(taxableFood) || 0) + (Number(cgstFood) || 0) + (Number(sgstFood) || 0) > 0;

  const taxableTotal = (Number(taxableCruise) || 0) + (Number(taxableFood) || 0);
  const totalCgst    = (Number(cgstCruise) || 0) + (Number(cgstFood) || 0);
  const totalSgst    = (Number(sgstCruise) || 0) + (Number(sgstFood) || 0);
  // Derive the display rates from the CN's OWN stored amounts, not current
  // settings — a credit note is a fixed legal document, and the proportional
  // split preserves the original invoice's rate exactly (numerator + denominator
  // scale by the same ratio), so this reconciles with the amounts forever.
  const deriveRate = (tax, taxable) => {
    const t = Number(tax) || 0, base = Number(taxable) || 0;
    if (base <= 0) return 0;
    return Math.round((t / base) * 100 * 100) / 100;  // % to 2dp
  };
  const cgstCruisePct = deriveRate(cgstCruise, taxableCruise);
  const cgstFoodPct   = deriveRate(cgstFood, taxableFood);
  const fmtPct = (n) => (Number.isInteger(n) ? n : n.toFixed(2));

  const paxParts = [
    Number(adults)   > 0 ? `${adults} Adults`     : null,
    Number(children) > 0 ? `${children} Children`  : null,
  ].filter(Boolean);
  const paxLabel = paxParts.join(', ') || '—';

  const kindLabel = kind === 'full' ? 'Full Reversal' : 'Partial Reversal';

  return (
    <div ref={ref} style={{
      width: '210mm',
      minHeight: '297mm',
      background: '#ffffff',
      fontFamily: 'Helvetica, Arial, sans-serif',
      boxSizing: 'border-box',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
      margin: '0 auto',
    }}>

      {/* ── Header ── */}
      <div style={{ background: NAVY, padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <img
            src="/alfresco-grand.png"
            alt="Alfresco Grand"
            style={{ height: 40, mixBlendMode: 'screen', display: 'block', marginBottom: 8 }}
          />
          <div style={{ color: GOLD, fontSize: 20, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase' }}>
            CREDIT NOTE
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#ffffff', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{companyName}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>{address}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>GSTIN: {gstin}</div>
          <div style={{ color: '#b0c4d8', fontSize: 9, lineHeight: 1.6 }}>
            {phone && `Ph: ${phone}`}{email && phone ? ` | ${email}` : email}
          </div>
        </div>
      </div>

      {/* ── Against-invoice reference banner ── */}
      <div style={{ background: '#f8f6f1', borderBottom: '1px solid #e8e0d0', padding: '9px 22px', fontSize: 10.5, color: NAVY }}>
        <span style={{ fontWeight: 700 }}>Against Invoice: </span>
        <span style={{ fontWeight: 700, color: GOLD }}>{invoiceNumber || '—'}</span>
        {invoiceDate && <span style={{ color: '#5a4a38' }}> dated {fmtYMD(invoiceDate)}</span>}
      </div>

      {/* ── Credit note + Bill To ── */}
      <div style={{ display: 'flex', padding: '16px 22px 12px', gap: 24, borderBottom: '1px solid #e8e0d0' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Credit Note Details</div>
          <InfoRow label="Credit Note No" value={creditNoteNumber} bold />
          <InfoRow label="Date"           value={fmtYMD(creditNoteDate)} />
          <InfoRow label="Type"           value={kindLabel} />
          <InfoRow label="Place of Supply" value="Assam (18)" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Credit To</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: NAVY, marginBottom: 3 }}>{customerName}</div>
          {isB2B && partner_address && String(partner_address).split('\n').map((line, i) => (
            line.trim() ? <div key={i} style={{ fontSize: 10.5, color: '#4a5a7a', lineHeight: 1.4 }}>{line}</div> : null
          ))}
          {isB2B && partner_state && (
            <div style={{ fontSize: 10.5, color: '#4a5a7a', marginTop: 2 }}>
              {partner_state}{partner_state_code ? ` (${partner_state_code})` : ''}
            </div>
          )}
          {isB2B && partner_gstin && (
            <div style={{ fontSize: 10.5, color: NAVY, fontWeight: 700, marginTop: 2, marginBottom: 2 }}>GSTIN: {partner_gstin}</div>
          )}
          {customerPhone && <div style={{ fontSize: 10.5, color: '#4a5a7a' }}>{customerPhone}</div>}
        </div>
      </div>

      <GoldDivider />

      {/* ── Booking details ── */}
      <div style={{ padding: '12px 22px 10px' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Booking Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 40px' }}>
          <InfoRow label="Cruise"     value={cruiseDisplayName || cruiseType} />
          <InfoRow label="Deck"       value={deck} />
          <InfoRow label="Passengers" value={paxLabel} />
        </div>
        {reason && reason.trim() && (
          <div style={{ marginTop: 4 }}>
            <InfoRow label="Reason" value={reason} bold />
          </div>
        )}
      </div>

      <GoldDivider />

      {/* ── Reversal tax summary ── */}
      <div style={{ padding: '14px 22px 0' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#7a6a50', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
          Amount Reversed
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th align="left">Description</Th>
              <Th align="center">SAC</Th>
              <Th align="right">Taxable Amount</Th>
              <Th align="center">CGST %</Th>
              <Th align="right">CGST Amt</Th>
              <Th align="center">SGST %</Th>
              <Th align="right">SGST Amt</Th>
              <Th align="right">Total Tax</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Cruise Charges</Td>
              <Td align="center">{sacCruise}</Td>
              <Td align="right">₹{f2(taxableCruise)}</Td>
              <Td align="center">{fmtPct(cgstCruisePct)}%</Td>
              <Td align="right">₹{f2(cgstCruise)}</Td>
              <Td align="center">{fmtPct(cgstCruisePct)}%</Td>
              <Td align="right">₹{f2(sgstCruise)}</Td>
              <Td align="right" bold>₹{f2((Number(cgstCruise) || 0) + (Number(sgstCruise) || 0))}</Td>
            </tr>
            {hasFood && (
              <tr>
                <Td shade>Food &amp; Beverage</Td>
                <Td align="center" shade>{sacFood}</Td>
                <Td align="right" shade>₹{f2(taxableFood)}</Td>
                <Td align="center" shade>{fmtPct(cgstFoodPct)}%</Td>
                <Td align="right" shade>₹{f2(cgstFood)}</Td>
                <Td align="center" shade>{fmtPct(cgstFoodPct)}%</Td>
                <Td align="right" shade>₹{f2(sgstFood)}</Td>
                <Td align="right" bold shade>₹{f2((Number(cgstFood) || 0) + (Number(sgstFood) || 0))}</Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Totals ── */}
      <div style={{ padding: '14px 22px', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ width: 300 }}>
          <TotalRow label="Taxable Amount"  value={f2(taxableTotal)} />
          <TotalRow label="Total CGST"      value={f2(totalCgst)} />
          <TotalRow label="Total SGST"      value={f2(totalSgst)} />
          <TotalRow label="Total Tax"       value={f2(totalTax)} />
          <div style={{ borderTop: `2px solid ${GOLD}`, marginTop: 6, paddingTop: 6 }}>
            <TotalRow label="Total Credited" value={fI(grandTotal)} big />
          </div>
        </div>
      </div>

      {/* Amount in words */}
      <div style={{ padding: '0 22px 16px' }}>
        <div style={{ background: '#f8f6f1', border: '1px solid #e8e0d0', borderRadius: 6, padding: '8px 14px', fontSize: 10, color: NAVY }}>
          <span style={{ fontWeight: 700 }}>Amount in words: </span>
          <span style={{ fontStyle: 'italic' }}>{amountToWords(grandTotal)}</span>
        </div>
      </div>

      <GoldDivider />

      {/* ── Footer ── */}
      <div style={{ padding: '14px 22px', textAlign: 'center' }}>
        <div style={{ fontSize: 9, color: '#9a8e7a', marginBottom: 4 }}>This is a computer generated credit note</div>
        <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 3 }}>Thank you for choosing Alfresco Grand</div>
        <div style={{ fontSize: 9, color: '#9a8e7a' }}>{companyName} | GSTIN: {gstin}</div>
      </div>
    </div>
  );
});

// ── Modal wrapper with print button (mirrors InvoicePrint) ────────────────────
export default function CreditNotePrint({ creditNoteData, onClose }) {
  const contentRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4 portrait; margin: 0; } body { margin: 0; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
    documentTitle: creditNoteData.creditNoteNumber,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(10,22,40,0.75)',
      display: 'flex', flexDirection: 'column',
      zIndex: 2100, overflow: 'hidden',
    }}>
      {/* Control bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px', background: NAVY, flexShrink: 0,
      }}>
        <div>
          <div style={{ color: GOLD, fontWeight: 800, fontSize: '0.95rem' }}>{creditNoteData.creditNoteNumber}</div>
          <div style={{ color: '#b0c4d8', fontSize: '0.78rem', marginTop: 1 }}>{creditNoteData.customerName}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handlePrint}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: GOLD, color: NAVY, border: 'none', borderRadius: 8,
              padding: '8px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Printer size={16} strokeWidth={2} aria-hidden="true" /> Print / Save PDF
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 8, padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Scrollable preview */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '24px 16px', background: '#3a3a4a' }}>
        <div style={{ background: '#ffffff', boxShadow: '0 8px 40px rgba(0,0,0,0.5)', width: '210mm', maxWidth: '100%' }}>
          <CreditNoteContent ref={contentRef} creditNoteData={creditNoteData} />
        </div>
      </div>
    </div>
  );
}
